import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'react-query';
import SectionService from '../../../../services/SectionService';
import MeetingService from '../../../../services/MeetingService';
import { processQueuedUploads } from '../../store/slices/uploadProgressSlice';
import { useDirtyHelpers } from '../../hooks/useUnsavedChanges';
import { useUiContext } from '../../../../context/UiContext';
import {
  setTrimModalData,
  setPreviewModalVisible,
  setUpdateMeetingTitleModalVisible,
  setPublishMeetingModalVisible,
  setPublishProgressModalVisible,
  setNotificationManagerVisible,
  setTextModalVisible,
  setEditingTextContext,
  setQuestionModalVisible,
  setFetchedQuestionData,
  setAssignSectionModalVisible,
  setAssignSectionErrorPopupVisible,
  setAssignSectionSuccessPopupVisible,
  setSaveSuccessModalVisible,
  setContributorClosedModalVisible,
  setCloseSectionSuccessModalVisible,
  setSaveBeforePublishModalVisible,
  setSelectShareOptionModalVisible,
  setPermissionManagerVisible,
  setRequestReviewModalVisible,
} from '../../store/slices/editorUISlice';
import {
  updateItemTrim,
  addTimelineItem,
} from '../../store/slices/timelineSlice';
import {
  setIsScreenRecordReminderVisible,
} from '../../store/slices/recordingSlice';
import { addAsset } from '../../store/slices/assetsSlice';
import ProductionPreviewModal from '../../modals/ProductionPreviewModal';
import ClientSideEditSectionModal from '../../modals/ClientSideEditSectionModal';
import UpdateMeetingTitleModal from '../../../../modals/UpdateMeetingTitleModal';
import PublishMeetingModal from '../../../../modals/PublishMeetingModal';
import NotificationManagerModal from '../../../../modals/NotificationManagerModal';
import TextEditorModal from '../../modals/TextEditorModal';
import CreateQuestionModal from '../../../../modals/CreateQuestionModal';
import FileUploadProgressModal from '../../../../modals/FileUploadProgressModal';
import SaveProgressModal from '../../../../modals/SaveProgressModal';
import AssignSectionModal from '../../../../modals/AssignSectionModal';
// import MultiButtonDialogModal from '../../../../modals/MultiButtonDialogModal'; // Commented out - modal is bypassed
import AlertModal from '../../../../modals/AlertModal';
import ConfirmActionModal from '../../../../modals/ConfirmActionModal';
import SelectShareOptionModal from '../../../../modals/SelectShareOptionModal';
import RequestReviewModal from '../../../../modals/RequestReviewModal';
import PlaceholderModal from '../../modals/PlaceholderModal';
import PermissionManager from '../../../../components/PermissionManager';
import { RESOURCE_TYPES } from '../../../../services/permissions/constants';
import {
  isFileSizeExceeded,
  getFileSizeErrorMessage,
} from '../../lib/uploadLimits';
import {
  setMobileUploading,
  setMobileUploadCompleted,
  resetMobileUpload,
} from '../../store/slices/mobileUploadSlice';

export default function ModalsContainer({
  // Props from parent
  meetingId,
  isEditMode,
  meetingTitle,
  setMeetingTitle,
  folderId,
  fetchedMeeting,
  fetchedSections,
  timelineItems,
  allAssets,
  footerTimelineRef,
  publish,
  isPublishing,
  publishProgress,
  refetchSections,
  handleCreateText,
  handleCreateQuestion,
  handleCreatePlaceholder,
  updateTextSection,
  updateQuestionSection,
  updatePlaceholder,
  editingQuestionContext,
  confirmScreenReminder,
  saveDraft,
  isSaving,
  saveProgress,
  saveProgressInfo,
  user,
  onConfirmLeave,
  onCancelLeave,
  canUpdateDraftData,
  isMobile,
  isMobileFileSelected,
  pendingMobileFileRef,
  handleVideoUpload,
  handleImageUpload, // eslint-disable-line no-unused-vars -- Keep for non-mobile usage
  setUploadProgress,
  isTemplateMode = false,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { markClean } = useDirtyHelpers();
  const { notifyError } = useUiContext();

  // Modal state - moved from parent
  const [previewStore, setPreviewStore] = useState(null);
  const [assignSectionErrorMessage, setAssignSectionErrorMessage] = useState('');
  // Store the meeting ID and sections from saveDraft to prevent duplicate creation on publish
  const [savedMeetingId, setSavedMeetingId] = useState(null);
  const [savedSections, setSavedSections] = useState(null);

  // Mutation for mobile image uploads (matches legacy createSection approach)
  const createMobileImageSection = useMutation(
    (values) => {
      if (values.files) {
        return SectionService.createAttachmentSections({
          ...values,
          prevSectionId: null, // Match legacy behavior
          setUploadProgress: values.setUploadProgress,
        });
      }
      return SectionService.createSection(values);
    },
    {
      onSuccess: () => {
        dispatch(setMobileUploadCompleted());
      },
      onError: (error) => {
        console.error('Mobile image upload failed:', error);
        notifyError(error?.response?.data?.message || 'Failed to upload image');
        dispatch(resetMobileUpload());
      },
    }
  );

  // Redux selectors for modal visibility
  // const editModalItemId = useSelector((state) => state.editorUI.editModalItemId); // Commented out - modal is bypassed
  const trimModalData = useSelector((state) => state.editorUI.trimModalData);
  const isPreviewModalVisible = useSelector((state) => state.editorUI.isPreviewModalVisible);
  const isUpdateMeetingTitleModalVisible = useSelector((state) => state.editorUI.isUpdateMeetingTitleModalVisible);
  const isPublishMeetingModalVisible = useSelector((state) => state.editorUI.isPublishMeetingModalVisible);
  const isPublishProgressModalVisible = useSelector((state) => state.editorUI.isPublishProgressModalVisible);
  const isNotificationManagerVisible = useSelector((state) => state.editorUI.isNotificationManagerVisible);
  const isTextModalVisible = useSelector((state) => state.editorUI.isTextModalVisible);
  const editingTextContextFromRedux = useSelector((state) => state.editorUI.editingTextContext);
  const isQuestionModalVisible = useSelector((state) => state.editorUI.isQuestionModalVisible);
  const fetchedQuestionData = useSelector((state) => state.editorUI.fetchedQuestionData);
  const isAssignSectionModalVisible = useSelector((state) => state.editorUI.isAssignSectionModalVisible);
  const isAssignSectionErrorPopupVisible = useSelector((state) => state.editorUI.isAssignSectionErrorPopupVisible);
  const isAssignSectionSuccessPopupVisible = useSelector((state) => state.editorUI.isAssignSectionSuccessPopupVisible);
  const isSaveSuccessModalVisible = useSelector((state) => state.editorUI.isSaveSuccessModalVisible);
  const isContributorClosedModalVisible = useSelector((state) => state.editorUI.isContributorClosedModalVisible);
  const isCloseSectionSuccessModalVisible = useSelector((state) => state.editorUI.isCloseSectionSuccessModalVisible);
  const isSaveBeforePublishModalVisible = useSelector((state) => state.editorUI.isSaveBeforePublishModalVisible);
  const isSelectShareOptionModalVisible = useSelector((state) => state.editorUI.isSelectShareOptionModalVisible);
  const isRequestReviewModalVisible = useSelector((state) => state.editorUI.isRequestReviewModalVisible);
  const isPermissionManagerVisible = useSelector((state) => state.editorUI.isPermissionManagerVisible);
  const permissionManagerResource = useSelector((state) => state.editorUI.permissionManagerResource);
  const isLeaveConfirmationVisible = useSelector((state) => state.editorUI.isLeaveConfirmationVisible);
  const isScreenRecordReminderVisible = useSelector((state) => state.recording.isScreenRecordReminderVisible);

  // Note: Removed auto-redirect for notify-owner flow - contributors stay on page

  // Redux selectors for section assignment
  const currentTime = useSelector((state) => state.playback.currentTime);
  const selectedItemId = useSelector((state) => state.timeline.selectedItemId);

  // Handler for assigned section (draft mode - adds to Redux only)
  const handleSectionAssigned = useCallback(({ email, message }) => {
    const assetId = `assigned-section-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const duration = 5; // Placeholder duration for assigned sections

    // Create asset
    const asset = {
      id: assetId,
      type: 'assigned-section',
      source_type: 'upload', // Indicates not yet saved to backend
      name: `Assigned to ${email}`,
      duration,
      metadata: {
        assignedEmail: email,
        message: message || '', // Store message for later use during save
        // assignedUserId and assignedName will be set after backend call during save
      },
    };

    // Add asset to Redux
    dispatch({
      ...addAsset(asset),
      meta: { undoable: true },
    });

    // Add timeline item
    const currentItem = timelineItems.find((item) => item.id === selectedItemId);
    dispatch({
      ...addTimelineItem({
        assetId: asset.id,
        duration,
        currentTime,
        anchorItemId: selectedItemId || currentItem?.id,
        metadata: {
          assignedEmail: email,
          message: message || '',
        },
      }),
      meta: { undoable: true },
    });
  }, [dispatch, currentTime, selectedItemId, timelineItems]);

  return (
    <>
      {/* Edit Section Modal */}
      {/* COMMENTED OUT: Modal is now bypassed - pencil icon directly opens trim modal */}
      {/* {editModalItemId && (
        <MultiButtonDialogModal
          show={!!editModalItemId}
          handleClose={() => dispatch(setEditModalItemId(null))}
          title={
            <>
              What would you like to do with
              <br /> this section?
            </>
          }
          buttons={[
            {
              title: 'Add Audio',
              handleClick: () => {},
              color: '',
              disabled: true,
            },
            {
              title: 'Remove Audio',
              handleClick: () => {},
              color: 'black',
              disabled: true,
            },
            {
              title: 'Trim Video',
              handleClick: () => {
                // Get the asset and item for this editModalItemId
                const item = timelineItems.find((i) => i.id === editModalItemId);
                if (item) {
                  const asset = allAssets.find((a) => a.id === item.asset_id);
                  if (asset && asset.type === 'video') {
                    // Calculate original duration (duration + trim_start + trim_end)
                    const trimStart = item.settings?.trim_start || 0;
                    const trimEnd = item.settings?.trim_end || 0;
                    const originalDuration =
                      asset.duration ||
                      item.duration + trimStart + trimEnd;
                    dispatch(setTrimModalData({
                      itemId: editModalItemId,
                      asset,
                      itemDuration: originalDuration,
                      existingTrimStart: trimStart,
                      existingTrimEnd: trimEnd,
                    }));
                  }
                }
                dispatch(setEditModalItemId(null));
              },
              color: '#FDB258',
              testId: 'trim-video-button',
            },
          ]}
        />
      )} */}

      {/* Trim Video Modal */}
      {trimModalData && (
        <ClientSideEditSectionModal
          show={!!trimModalData}
          handleClose={() => dispatch(setTrimModalData(null))}
          title="Trim Video"
          asset={trimModalData.asset}
          itemId={trimModalData.itemId}
          itemDuration={trimModalData.itemDuration}
          existingTrimStart={trimModalData.existingTrimStart}
          existingTrimEnd={trimModalData.existingTrimEnd}
          onSave={({ startTime, endTime, originalDuration }) => {
            dispatch({
              ...updateItemTrim({
                itemId: trimModalData.itemId,
                trimStart: startTime,
                trimEnd: originalDuration - endTime,
                originalDuration,
              }),
              meta: { undoable: true },
            });
          }}
        />
      )}

      {/* Production Preview Modal */}
      {isPreviewModalVisible && previewStore && (
        <ProductionPreviewModal
          show={isPreviewModalVisible}
          handleClose={() => {
            dispatch(setPreviewModalVisible(false));
            setPreviewStore(null);
          }}
          store={previewStore}
        />
      )}

      {/* Update Meeting Title Modal */}
      <UpdateMeetingTitleModal
        show={isUpdateMeetingTitleModalVisible}
        heading={isTemplateMode ? 'Give your template a name' : undefined}
        placeholder={isTemplateMode ? 'Untitled template' : undefined}
        cancelNavigateTo={isTemplateMode ? '/templates' : undefined}
        onConfirm={async (title) => {
          // Check if this is a mobile upload flow
          if (isMobile && isMobileFileSelected) {
            // Mobile upload flow: create meeting with 'mobile uploads' type and upload
            try {
              // 1. Create meeting with mobile uploads type
              const newMeeting = await MeetingService.createMeeting({
                title,
                folderId: folderId || null,
                type: 'mobile uploads',
              });
              
              // 2. Mark as uploading state
              dispatch(setMobileUploading(newMeeting.id));
              
              const pendingFileRef = pendingMobileFileRef;
              const pendingFile = pendingFileRef.current;
              
              if (pendingFile) {
                const { file, fileType } = pendingFile;
                // Clear the ref
                pendingFileRef.current = null;
                
                if (fileType === 'image') {
                  if (isFileSizeExceeded(file?.size)) {
                    notifyError(getFileSizeErrorMessage(file?.name, file?.size));
                    dispatch(resetMobileUpload());
                    dispatch(setUpdateMeetingTitleModalVisible(false));
                    return;
                  }
                  // For images: use mutation approach (same as legacy createSection)
                  createMobileImageSection.mutate({
                    title: file.name,
                    meetingId: newMeeting.id,
                    files: [file],
                    type: 'image',
                    setUploadProgress, // Use the passed progress callback
                  });
                } else {
                  // For videos: use background upload flow
                  // Queue the file using existing background upload handlers
                  const acceptedCount = await handleVideoUpload([file]);
                  if (!acceptedCount) {
                    dispatch(resetMobileUpload());
                    dispatch(setUpdateMeetingTitleModalVisible(false));
                    return;
                  }
                  // Process the queued upload with the new meeting ID
                  dispatch(processQueuedUploads(newMeeting.id));
                }
              }
              
              dispatch(setUpdateMeetingTitleModalVisible(false));
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create meeting';
              notifyError(errorMessage);
              dispatch(resetMobileUpload());
              // Don't close modal on error - let user retry
            }
            return;
          }
          
          // Template mode: just set the title, no meeting creation needed
          if (isTemplateMode) {
            setMeetingTitle(title);
            dispatch(setUpdateMeetingTitleModalVisible(false));
            return;
          }

          // If in create mode and no meeting exists, create it first
          if (!isEditMode && !meetingId) {
            try {
              const newMeeting = await MeetingService.createMeeting({
                title,
                folderId: folderId || null,
                type: 'my videos',
              });
              
              // Mark as clean to prevent "Leave without saving?" prompt
              markClean();
              
              // Navigate to edit mode URL (same pattern as Save Draft)
              navigate(`/my-videos/${newMeeting.id}/edit`, { replace: true });
              
              // Process queued uploads now that we have a meetingId
              dispatch(processQueuedUploads(newMeeting.id));
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create meeting';
              notifyError(errorMessage);
              return; // Don't close modal on error
            }
          } else {
            // Edit mode - just update title
            setMeetingTitle(title);
          }
          
          dispatch(setUpdateMeetingTitleModalVisible(false));
        }}
        handleClose={() => {
          // For mobile upload, cancel the upload if closing without title
          if (isMobile && isMobileFileSelected) {
            dispatch(resetMobileUpload());
            dispatch(setUpdateMeetingTitleModalVisible(false));
            return;
          }
          
          // Only prevent closing if no title is set (first load)
          if (!meetingTitle) {
            return;
          }
          dispatch(setUpdateMeetingTitleModalVisible(false));
        }}
        title={meetingTitle}
      />

      {/* Publish Meeting Modal */}
      <PublishMeetingModal
        show={isPublishMeetingModalVisible}
        handleClose={() => {
          dispatch(setPublishMeetingModalVisible(false));
          // Clear saved meeting ID and sections when closing publish modal
          setSavedMeetingId(null);
          setSavedSections(null);
        }}
        meetingId={isEditMode ? meetingId : savedMeetingId || null}
        meetingTitle={meetingTitle}
        updateMeeting={async ({ comment }) => {
          dispatch(setPublishMeetingModalVisible(false));
          try {
            // Use savedMeetingId if available (from saveDraft), otherwise use meetingId prop
            // This prevents duplicate meeting creation when publishing for the first time
            const actualMeetingId = savedMeetingId || (isEditMode ? meetingId : null);
            // Use savedSections if available (from saveDraft), otherwise use fetchedSections
            // This prevents duplicate section creation when publishing for the first time
            const sectionsToUse = savedSections || (isEditMode ? fetchedSections : null);

            await publish({
              title: meetingTitle,
              folderId: fetchedMeeting?.folderId || folderId,
              comment,
              assets: allAssets,
              timelineItems,
              meetingId: actualMeetingId,
              fetchedSections: sectionsToUse,
              pendingSaveItemIds: footerTimelineRef.current?.getPendingSaveItemIds() || new Set(),
            });
            // Clear saved meeting ID and sections after successful publish
            setSavedMeetingId(null);
            setSavedSections(null);
          } catch (error) {
            // Error already handled by usePublish hook
          }
        }}
      />

      {/* Notification Manager Modal */}
      <NotificationManagerModal
        reminder={fetchedMeeting?.EmailReminder}
        meetingTitle={fetchedMeeting?.title || meetingTitle}
        show={isNotificationManagerVisible}
        handleClose={() => dispatch(setNotificationManagerVisible(false))}
        meetingId={isEditMode ? meetingId : savedMeetingId || null}
      />
      <SelectShareOptionModal
        show={isSelectShareOptionModalVisible}
        handleClose={() => dispatch(setSelectShareOptionModalVisible(false))}
        openRequestReviewModal={() => {
          dispatch(setRequestReviewModalVisible(true));
          dispatch(setSelectShareOptionModalVisible(false));
        }}
        openShareMeetingModal={() => {
          dispatch(setPermissionManagerVisible(true));
          dispatch(setSelectShareOptionModalVisible(false));
        }}
      />
      <RequestReviewModal
        show={isRequestReviewModalVisible}
        handleClose={() => dispatch(setRequestReviewModalVisible(false))}
        meetingId={meetingId}
      />
      <PermissionManager
        show={isPermissionManagerVisible}
        onClose={() => dispatch(setPermissionManagerVisible(false))}
        resourceId={
          permissionManagerResource?.resourceId || fetchedMeeting?.id || meetingId
        }
        resourceType={RESOURCE_TYPES.MEETING}
        resourceName={
          permissionManagerResource?.resourceName ||
          fetchedMeeting?.title ||
          meetingTitle
        }
        user={user}
        folderId={fetchedMeeting?.folderId || folderId}
        folderUserRole={fetchedMeeting?.folderUserRole || null}
      />

      {/* Text Editor Modal */}
      <TextEditorModal
        show={isTextModalVisible}
        initialHtml={editingTextContextFromRedux?.initialHtml || ''}
        onSave={(html) => {
          if (editingTextContextFromRedux?.sectionId || editingTextContextFromRedux?.assetId) {
            updateTextSection({ sectionId: editingTextContextFromRedux?.sectionId || '', text: html });
          } else {
            handleCreateText(html);
          }
        }}
        onClose={() => {
          dispatch(setTextModalVisible(false));
          dispatch(setEditingTextContext(null));
        }}
      />

      {/* Create Question Modal */}
      <CreateQuestionModal
        show={isQuestionModalVisible}
        setQuestion={handleCreateQuestion}
        fetchedQuestionData={fetchedQuestionData}
        setIsQuestionFormVisible={(value) => dispatch(setQuestionModalVisible(value))}
        setFetchedQuestionData={(value) => dispatch(setFetchedQuestionData(value))}
        hidePlaceholder={() => {}}
        updateQuestionSection={({ sectionId, question }) =>
          updateQuestionSection({
            sectionId: sectionId || editingQuestionContext?.sectionId || '',
            question,
          })
        }
      />

      {/* File Upload Progress Modal */}
      <FileUploadProgressModal
        show={isPublishProgressModalVisible}
        handleClose={() => {
          dispatch(setPublishProgressModalVisible(false));
        }}
        uploadProgress={publishProgress.percentage}
        isSectionBeingCreated={isPublishing}
        setUploadProgress={() => {}}
      />

      {/* Save Draft Progress Modal */}
      <SaveProgressModal
        show={isSaving}
        handleClose={() => {
          // Don't allow closing during save
        }}
        progress={saveProgress}
        sectionIndex={saveProgressInfo.sectionIndex}
        totalSections={saveProgressInfo.totalSections}
        operation={saveProgressInfo.operation}
      />

      {/* Assign Section Modal */}
      <AssignSectionModal
        show={isAssignSectionModalVisible}
        handleClose={() => {
          dispatch(setAssignSectionModalVisible(false));
        }}
        meetingId={meetingId || null}
        refetchSections={refetchSections}
        setIsAssignSectionPopupVisible={(value) => dispatch(setAssignSectionErrorPopupVisible(value))}
        setAssignSectionErrorMessage={setAssignSectionErrorMessage}
        setIsAssignSectionSuccessPopupVisible={(value) => dispatch(setAssignSectionSuccessPopupVisible(value))}
        hidePlaceholder={() => {}}
        onSectionAssigned={handleSectionAssigned}
      />

      {/* Assign Section Error Alert */}
      {isAssignSectionErrorPopupVisible && (
        <AlertModal
          show={isAssignSectionErrorPopupVisible}
          message={assignSectionErrorMessage}
          hasCloseButton
          handleClose={() => dispatch(setAssignSectionErrorPopupVisible(false))}
        />
      )}

      {/* Assign Section Success Alert */}
      {isAssignSectionSuccessPopupVisible && (
        <AlertModal
          show={isAssignSectionSuccessPopupVisible}
          title="Section Added to Draft"
          message="The section will be assigned when you save the draft. You will be notified when the collaborator completes the section(s)."
          hasCloseButton
          hasConfirmButton
          label="OK"
          handleClose={() => dispatch(setAssignSectionSuccessPopupVisible(false))}
          onConfirm={() => dispatch(setAssignSectionSuccessPopupVisible(false))}
        />
      )}

      {/* Save Success Alert */}
      {isSaveSuccessModalVisible && (
        <AlertModal
          show={isSaveSuccessModalVisible}
          title="Draft saved"
          message="Your video has been saved as a draft. It has not been published yet. To share it with others, click the 'Publish' button."
          hasCloseButton
          hasConfirmButton
          label="OK"
          handleClose={() => {
            dispatch(setSaveSuccessModalVisible(false));
          }}
          onConfirm={() => {
            dispatch(setSaveSuccessModalVisible(false));
          }}
        />
      )}

      {/* Contributor Closed Alert */}
      {isContributorClosedModalVisible && (
        <AlertModal
          show={isContributorClosedModalVisible}
          title="Contribution Completed"
          message="Your assigned section has been closed. You can no longer add or modify content in this video."
          hasCloseButton
          handleClose={() => {
            dispatch(setContributorClosedModalVisible(false));
            // Redirect to viewer page
            if (meetingId) {
              navigate(`/my-videos/${meetingId}`);
            }
          }}
        />
      )}

      {/* Close Section Success Modal */}
      {isCloseSectionSuccessModalVisible && (
        <AlertModal
          show={isCloseSectionSuccessModalVisible}
          title="Owner Notified"
          message="The owner has been notified about your addition. You can continue editing if needed."
          hasCloseButton
          handleClose={() => {
            dispatch(setCloseSectionSuccessModalVisible(false));
          }}
        />
      )}

      {/* Screen Record Reminder Alert */}
      <AlertModal
        show={isScreenRecordReminderVisible}
        title="You're getting ready to share your screen"
        message="After selecting a screenshare option, video/audio recording will start in three seconds. Come back to the app to stop recording when done."
        label="Select an option and start recording"
        hasCloseButton
        hasConfirmButton
        handleClose={() => {
          dispatch(setIsScreenRecordReminderVisible(false));
        }}
        onConfirm={() => {
          confirmScreenReminder();
        }}
      />

      {/* Publish Video Confirmation Modal */}
      <ConfirmActionModal
        show={isSaveBeforePublishModalVisible}
        title="Publish Video"
        message="All changes will be saved, and your video will be published."
        handleClose={() => {
          dispatch(setSaveBeforePublishModalVisible(false));
        }}
        onConfirm={async () => {
          try {
            // Close the confirmation modal
            dispatch(setSaveBeforePublishModalVisible(false));

            // Save draft first (skip success modal during publish flow)
            const savedMeeting = await saveDraft({
              title: meetingTitle,
              folderId: fetchedMeeting?.folderId || folderId,
              meetingId: isEditMode ? meetingId : null,
              assets: allAssets,
              timelineItems,
              userRole: fetchedMeeting?.userRole,
              userId: user?.id,
              canUpdateDraftData,
              skipRefetchAfterSave: true, // avoid reloading stale sections before publish
              skipSuccessModal: true, // Skip showing "Draft saved" modal
            });

            // Store the meeting ID from saveDraft to use in publish
            // This prevents duplicate meeting creation when publishing for the first time
            if (savedMeeting?.id) {
              setSavedMeetingId(savedMeeting.id);
              
              // Fetch sections that were just created by saveDraft
              // This prevents duplicate section creation when publishing
              try {
                const sectionsFromDraft = await SectionService.getSections(savedMeeting.id);
                setSavedSections(sectionsFromDraft || []);
              } catch (error) {
                console.error('Failed to fetch sections after saveDraft:', error);
                setSavedSections([]);
              }
            }

            // Refetch sections to ensure UI has latest data
            // If a new meeting was created, we need to refetch sections
            const actualMeetingId = savedMeeting?.id || (isEditMode ? meetingId : null);
            if (actualMeetingId && refetchSections) {
              await refetchSections();
            }

            // After successful save, proceed directly to publish modal
            dispatch(setPublishMeetingModalVisible(true));
          } catch (error) {
            // Error is already handled by useSaveDraft hook
            console.error('Failed to save before publish:', error);
          }
        }}
      />

      {/* Leave Confirmation Modal */}
      <ConfirmActionModal
        show={isLeaveConfirmationVisible}
        title="Leave without saving?"
        message="You have unsaved changes. Leaving this page will discard your edits."
        handleClose={onCancelLeave}
        onConfirm={onConfirmLeave}
      />

      <PlaceholderModal
        onSave={({ title, description, isEditing }) => {
          if (isEditing) {
            updatePlaceholder({ title, description });
            return;
          }

          handleCreatePlaceholder({ title, description });
        }}
      />
    </>
  );
}
